import formatAddress from 'modules/addresses/util/format-address';
import {OrderProps, OrderStatus, OrderTypeEnum} from 'modules/orders/types';
import React from 'react';
import {FaWhatsapp} from 'react-icons/fa';
import IconButton from 'shared/migration-components/icon-button';
import dateFnsService from 'shared/services/date-fns-service';
import styled, {useTheme} from 'styled-components';
import DownloadReceiptButton from './download-receipt-button';
import {GiFullMotorcycleHelmet} from 'react-icons/gi';
import DeliveryManSelect from 'modules/delivery-men/components/delivery-man-select';
import toBrl from 'shared/util/to-brl';
import NextStatusButton from './next-status-button';
import Row from 'shared/components/row';
import {LightText, MediumText} from 'shared/components/text';
import Column from 'shared/components/column';
import {IoCheckmarkCircle, IoCloseCircle} from 'react-icons/io5';
import {useNavigate} from 'react-router-dom';

const OrderRow: React.FC<
  OrderProps & {
    rowColor: string;
    rowHoverColor: string;
  }
> = ({rowColor, rowHoverColor, ...order}) => {
  const {colors} = useTheme();
  const navigate = useNavigate();

  const {
    id,
    number,
    guestUser,
    address,
    payment,
    paidTotal,
    deliveryPrice,
    type,
    currentStatusName,
    createdAt,
    freeDelivery,
    currentStatus,
    user,
    company,
    deliveryMan,
    changeSent,
  } = order;

  const updatedAt = currentStatus.date;

  const getGreetings = () => {
    const hora = new Date().getHours();
    if (hora >= 5 && hora < 12) {
      return 'Bom dia';
    } else if (hora >= 12 && hora < 18) {
      return 'Boa tarde';
    } else {
      return 'Boa noite';
    }
  };

  const handleGetWhatsappMessageToCompany = (order: OrderProps) => {
    const {number, type} = order;

    const messages = {
      opened: `Olá ${getGreetings()}!! O pedido ${number} já está sendo preparado?`,
      preparing: `Olá ${getGreetings()}!! O pedido ${number} já está pronto para ${
        type === OrderTypeEnum.delivery ? 'entrega' : 'retirada'
      }?`,
      ready_to_pickup: `Olá ${getGreetings()}!! O pedido ${number} já foi retirado pelo cliente?`,
      ready_to_delivery: `Olá ${getGreetings()}!! O pedido ${number} já saiu para a entrega?`,
      delivering: `Olá ${getGreetings()}!! O pedido ${number} já foi entregue?`,
    } as Record<Partial<OrderStatus>, string>;

    const message = messages[currentStatusName];

    return message ? `?text=${message}` : '';
  };

  return (
    <TableRow
      key={id}
      onClick={() => navigate(id)}
      {...{
        background: rowColor,
        hoverBackground: rowHoverColor,
      }}>
      <TableColumn>{number}</TableColumn>
      <TableColumn
        onClick={(e) => {
          e.stopPropagation();
          window.open(`https://wa.me/55${user?.phone ?? guestUser?.phone}`);
        }}>
        <div style={{display: 'flex', alignItems: 'center', gap: 5}}>
          {guestUser?.name ?? user?.name}
          <IconButton
            backgroundColor="#128c7e"
            icon={<FaWhatsapp color="rgba(255, 255, 255, .7)" />}
          />
        </div>
        {guestUser?.phone ?? user?.phone}
        {address && <div>{formatAddress(address, false)}</div>}
      </TableColumn>
      <TableColumn>
        <div>{payment.name}</div>
        {payment.change && (
          <Row gap={5}>
            <div>Troco para: {toBrl(payment.change)}</div>
            {changeSent && (
              <IoCheckmarkCircle
                style={{marginTop: -5}}
                color={colors.success}
                size={22}
              />
            )}
            {!changeSent && (
              <IoCloseCircle
                style={{marginTop: -5}}
                color={colors.error}
                size={22}
              />
            )}
          </Row>
        )}
        {payment.pixChange && (
          <div>Pix para troco: {`: ${payment.pixChange}`}</div>
        )}
      </TableColumn>
      <TableColumn
        onClick={(e) => {
          e.stopPropagation();
          window.open(
            `https://wa.me/55${company.phone.replace(
              /\D/g,
              '',
            )}${handleGetWhatsappMessageToCompany(order)}`,
          );
        }}>
        <Row gap={10} style={{padding: '0 10px'}}>
          {company.logo && (
            <img
              src={company.logo}
              style={{width: 40, height: 40, borderRadius: '50%'}}
            />
          )}
          <div>
            <Row gap={5}>
              <Column>
                <MediumText size="small">{company.name}</MediumText>
                <LightText size="small">
                  {formatAddress(company.address, false)}
                </LightText>
              </Column>
              <IconButton
                backgroundColor="#128c7e"
                icon={<FaWhatsapp color="rgba(255, 255, 255, .7)" />}
              />
            </Row>
          </div>
        </Row>
      </TableColumn>
      <TableColumn onClick={(e) => e.stopPropagation()}>
        <DeliveryManSelect orderId={id} deliveryManId={deliveryMan?.id}>
          {deliveryMan?.name ?? (
            <IconButton
              backgroundColor={colors.info}
              icon={<GiFullMotorcycleHelmet />}
              label="Vincular entregador"
              tooltip="Vincular entregador"
            />
          )}
        </DeliveryManSelect>
      </TableColumn>
      <TableColumn>
        <div>{dateFnsService.dateTimeFormat(createdAt)}</div>
        <div>{dateFnsService.formatDistanceFromNow(createdAt)}</div>
      </TableColumn>
      <TableColumn>
        {updatedAt && <div>{dateFnsService.dateTimeFormat(updatedAt)}</div>}
        {updatedAt && <b>{dateFnsService.formatDistanceFromNow(updatedAt)}</b>}
      </TableColumn>
      <TableColumn>{toBrl(Math.max(paidTotal, 0))}</TableColumn>
      <TableColumn>
        {freeDelivery ? 'Grátis' : toBrl(deliveryPrice)}
      </TableColumn>
      <TableColumn onClick={(e) => e.stopPropagation()}>
        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
          <NextStatusButton
            key={id}
            {...{
              type: type,
              currentStatusName,
              orderId: id,
              company: company,
            }}
          />
          {/* <IconButton
            icon={<IoPrint />}
            backgroundColor="rgba(128, 128, 128, .6)"
            color="white"
            label="Imprimir pedido1"
            onClick={() => handlePrint(order)}
          /> */}
          <DownloadReceiptButton orderId={id} />
        </div>
      </TableColumn>
    </TableRow>
  );
};

export default OrderRow;

interface TableRowProps {
  background: string;
  hoverBackground: string;
}

const TableRow = styled.tr<TableRowProps>`
  cursor: pointer;
  background-color: ${(props) => props.background};

  &:hover {
    background-color: ${(props) => props.hoverBackground};
  }
`;

export const TableColumn = styled.td`
  padding: 5px;
  border: 1px solid rgba(128, 128, 128, 0.4);
`;
